import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ClipboardCheck, Landmark, ChevronDown, ChevronUp, ShieldCheck, User } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Founder-only sidebar card. Lists staff who have submitted their onboarding
 * profile + signed documents and are waiting on final approval. Founders can
 * expand to review, then finalize. Silently hides for non-founders (403).
 */
export default function OnboardingReviewCard() {
  const [rows, setRows] = useState(null); // null | "denied" | []
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState({});

  const load = () => {
    api.get("/admin-auth/onboarding/pending")
      .then(({ data }) => setRows(data.pending || []))
      .catch((e) => { if (e?.response?.status === 403) setRows("denied"); });
  };
  useEffect(() => {
    load();
    const id = setInterval(() => { if (document.visibilityState === "visible") load(); }, 45000);
    return () => clearInterval(id);
  }, []);

  const finalize = async (userId, name) => {
    setBusy((b) => ({ ...b, [userId]: true }));
    try {
      await api.patch(`/admin-auth/onboarding/${userId}/finalize`);
      toast.success(`${name} is now cleared to work.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not finalize.");
    } finally {
      setBusy((b) => { const { [userId]: _, ...rest } = b; return rest; });
    }
  };

  if (rows === "denied" || rows === null || (Array.isArray(rows) && rows.length === 0)) {
    return null;
  }

  return (
    <div data-testid="onboarding-review-card" className="rounded-xl border border-[#a3823f]/40 bg-[#a3823f]/8 px-3 py-3">
      <div className="flex items-center gap-1.5 mb-2">
        <ClipboardCheck className="h-3.5 w-3.5 text-[#a3823f]" />
        <p className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f]">
          Onboarding review
          <span className="ml-1.5 tabular-nums text-[#1e3d3a]">({rows.length})</span>
        </p>
      </div>
      <ul className="space-y-2">
        {rows.map((r) => {
          const p = r.profile || {};
          const isOpen = expanded === r.user_id;
          return (
            <li key={r.user_id} data-testid={`onboarding-row-${r.username}`} className="rounded-md bg-white/70 border border-[#1e3d3a]/8 p-2">
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : r.user_id)}
                className="w-full flex items-center justify-between gap-1 text-left"
                data-testid={`onboarding-toggle-${r.username}`}
              >
                <div className="min-w-0">
                  <p className="text-[11px] font-medium text-[#1e3d3a] truncate">{p.legal_name || r.username}</p>
                  <p className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/50 truncate">@{r.username}</p>
                </div>
                {isOpen ? <ChevronUp className="h-3 w-3 text-[#1e3d3a]/60" /> : <ChevronDown className="h-3 w-3 text-[#1e3d3a]/60" />}
              </button>

              {isOpen && (
                <div className="mt-2 pt-2 border-t border-[#1e3d3a]/6 space-y-1.5 text-[10px] text-[#1e3d3a]/75">
                  <Row icon={User} label="Phone" val={p.phone} />
                  <Row icon={User} label="Email" val={p.email} />
                  <Row icon={User} label="Address" val={`${p.address_line1 || ""}${p.city ? ", " + p.city : ""} ${p.state || ""} ${p.postal_code || ""}`.trim() || "—"} />
                  <Row icon={Landmark} label="Bank" val={`${p.bank_name || "—"} · ${p.bank_account_type || "—"}`} />
                  <Row icon={Landmark} label="Routing" val={p.bank_routing || "—"} />
                  <Row icon={Landmark} label="Account" val={p.bank_account || (r.bank_last4 ? `••••${r.bank_last4}` : "—")} />
                  <Row icon={ShieldCheck} label="Signed docs" val={`${Object.values(r.signed_docs || {}).filter(v=>v?.agreed).length} / 5`} />
                  <Row icon={ShieldCheck} label="Signature" val={r.typed_signature || "—"} />
                  <button
                    type="button"
                    onClick={() => finalize(r.user_id, p.legal_name || r.username)}
                    disabled={busy[r.user_id]}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1 rounded-full bg-[#6b7c3a] text-white px-2 py-1.5 text-[10px] uppercase tracking-[0.2em] hover:bg-[#4d5a24] disabled:opacity-60 transition-colors duration-300"
                    data-testid={`onboarding-finalize-${r.username}`}
                  >
                    {busy[r.user_id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                    Finalize &amp; unlock
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Row({ icon: Icon, label, val }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <Icon className="h-2.5 w-2.5 text-[#a3823f] shrink-0" />
      <span className="uppercase tracking-[0.18em] text-[9px] text-[#1e3d3a]/50">{label}</span>
      <span className="ml-auto text-right truncate">{val || "—"}</span>
    </div>
  );
}
